import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useInViewOnce } from '../hooks/useInViewOnce';
import './RyanMonthlyLineChart.css';

// Gráfico de linha mensal compartilhado — extraído do gráfico "Evolução anual"
// já usado na aba Vendas do admin (OverviewTab, RyanAdminSales.tsx), pra manter
// exatamente o mesmo layout/eixos/animação de traço desenhado em qualquer lugar
// que precisar de uma evolução mensal (aba Vendas do admin e Painel do Embaixador).

export interface MonthPoint {
  month: string;
  salesCount: number;
  valueCents: number;
}

interface Props {
  data: MonthPoint[];
  year?: number;
  onYearChange?: (year: number) => void;
  formatValue?: (cents: number) => string;
  valueNoun?: string;
}

const defaultFormat = (c: number) => `R$ ${(c / 100).toFixed(2).replace('.', ',')}`;

const W = 700, H = 220, PAD_L = 60, PAD_R = 20, PAD_T = 20, PAD_B = 40;
const CHART_W = W - PAD_L - PAD_R;
const CHART_H = H - PAD_T - PAD_B;

const RyanMonthlyLineChart: React.FC<Props> = ({ data, year, onYearChange, formatValue = defaultFormat, valueNoun = 'venda' }) => {
  const lineRef = useRef<SVGPolylineElement>(null);
  const { ref: wrapRef, inView } = useInViewOnce<HTMLDivElement>();
  const [hoveredDot, setHoveredDot] = useState<number | null>(null);

  const maxValue = Math.max(...data.map(d => d.valueCents), 1);
  const pts = data.map((d, i) => ({
    x: PAD_L + (data.length <= 1 ? CHART_W / 2 : (i / (data.length - 1)) * CHART_W),
    y: PAD_T + CHART_H - (d.valueCents / maxValue) * CHART_H,
    ...d,
  }));
  const polyline = pts.map(p => `${p.x},${p.y}`).join(' ');
  const hoveredPt = hoveredDot !== null ? pts[hoveredDot] : null;

  // Animação de traço desenhado via comprimento real do path: mede o
  // comprimento, zera o offset sem transição, e só então anima até 0 num
  // frame seguinte. Só dispara quando o gráfico entra no viewport (useInViewOnce,
  // mesmo mecanismo do RyanWeeklyBarChart) — antes disparava assim que os dados
  // chegavam da API, o que normalmente acontece com o gráfico ainda fora de
  // tela (abaixo do fold), gastando a animação antes do usuário rolar até ela.
  useEffect(() => {
    if (!inView) return;
    const el = lineRef.current;
    if (!el) return;
    const len = el.getTotalLength();
    el.style.transition = 'none';
    el.style.strokeDasharray = `${len}`;
    el.style.strokeDashoffset = `${len}`;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.transition = 'stroke-dashoffset 2.8s cubic-bezier(0.16, 1, 0.3, 1)';
        el.style.strokeDashoffset = '0';
      });
    });
  }, [inView, data]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="rmlc-root">
      {onYearChange && year !== undefined && (
        <div className="rmlc-year-nav">
          <button className="rmlc-year-btn" onClick={() => onYearChange(year - 1)}><ChevronLeft size={14} /></button>
          <span className="rmlc-year-label">{year}</span>
          <button className="rmlc-year-btn" onClick={() => onYearChange(year + 1)}><ChevronRight size={14} /></button>
        </div>
      )}
      <div className="rmlc-wrap" ref={wrapRef}>
        <svg viewBox={`0 0 ${W} ${H}`} className="rmlc-svg">
          <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PAD_T + CHART_H} stroke="#e2e8f0" strokeWidth={1} />
          <line x1={PAD_L} y1={PAD_T + CHART_H} x2={PAD_L + CHART_W} y2={PAD_T + CHART_H} stroke="#e2e8f0" strokeWidth={1} />
          {[0, 0.25, 0.5, 0.75, 1].map(f => (
            <g key={f}>
              <line x1={PAD_L} x2={PAD_L + CHART_W} y1={PAD_T + CHART_H - f * CHART_H} y2={PAD_T + CHART_H - f * CHART_H} stroke="#f1f5f9" strokeWidth={1} />
              <text x={PAD_L - 6} y={PAD_T + CHART_H - f * CHART_H + 4} textAnchor="end" fontSize={9} fill="#94a3b8">
                {formatValue(Math.round(maxValue * f))}
              </text>
            </g>
          ))}
          {pts.length > 1 && (
            <polyline ref={lineRef} points={polyline}
              fill="none" stroke="#0D3B0E" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
          )}
          {pts.map((p, i) => (
            <g key={i} onMouseEnter={() => setHoveredDot(i)} onMouseLeave={() => setHoveredDot(null)}>
              <circle cx={p.x} cy={p.y} r={12} fill="transparent" style={{ cursor: 'pointer' }} />
              <circle cx={p.x} cy={p.y} r={hoveredDot === i ? 5.5 : 4} fill="#0D3B0E" />
              <text x={p.x} y={H - 8} textAnchor="middle" fontSize={9} fill="#64748b">{p.month}</text>
            </g>
          ))}
          {hoveredPt && (() => {
            const ttW = 96, ttH = 36;
            const ttX = Math.max(PAD_L, Math.min(hoveredPt.x - ttW / 2, W - PAD_R - ttW));
            const ttY = hoveredPt.y < PAD_T + ttH + 14 ? hoveredPt.y + 12 : hoveredPt.y - ttH - 10;
            return (
              <g key="tooltip" pointerEvents="none">
                <rect x={ttX} y={ttY} width={ttW} height={ttH} rx={5} fill="#0D3B0E" opacity={0.96} />
                <text x={ttX + ttW / 2} y={ttY + 14} textAnchor="middle" fontSize={10} fill="#fff" fontWeight="700">
                  {formatValue(hoveredPt.valueCents)}
                </text>
                <text x={ttX + ttW / 2} y={ttY + 28} textAnchor="middle" fontSize={8.5} fill="rgba(255,255,255,0.7)">
                  {hoveredPt.salesCount} {valueNoun}{hoveredPt.salesCount !== 1 ? 's' : ''}
                </text>
              </g>
            );
          })()}
        </svg>
      </div>
    </div>
  );
};

export default RyanMonthlyLineChart;
